import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";

import { formTemplateType, HTTP_STATUS, HttpStatusCode, FormTemplateProps, SubmitFormDataProps } from "./types";
import { useConfiguration } from "../../contexts/ConfigurationContext";
import { publicDataDomain } from "../../util/config";

const useRegistrationGuard = () => {
    const { currentMediaId, accountId, mediasData, configParams, setShowRegistrationForm } = useConfiguration();

    const currentMediaData = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
    const hasRegistration = currentMediaData && currentMediaData.links?.length > 0;
    const mediaDescription = '';

    const params = new URLSearchParams(window.location.search);
    let token = params.get('token');
    if (!token) {
        token = configParams?.formToken;
    }

    const [isTokenValid, setIsTokenValid] = useState<boolean>(false);
    // start as validating when there is a token, so the form isn't shown before the token is checked
    const [isValidatingToken, setIsValidatingToken] = useState<boolean>(!!token);
    const [formTemplate, setFormTemplate] = useState<formTemplateType>();
    const lastFetchedKeyRef = useRef<string | null>(null);

    const hasEventStarted = useMemo(() => {
        const startDate = currentMediaData?.custom ? currentMediaData?.custom['event-start'] : '';
        if (!startDate) {
            return false;
        }
        const now = moment();
        return moment(startDate).isBefore(now) || moment(startDate).isSame(now);
    }, [currentMediaData])

    const formId = useMemo(() => {
        if ((currentMediaData?.links ?? []).length === 0) {
            return '';
        }
        const formLink = currentMediaData.links.find((link: { id: string; type: string }) => link.type === 'Form');
        return formLink?.id || '';
    }, [currentMediaData]);

    const validateRegistrationToken = async ({ accountId, formId, payload }: SubmitFormDataProps): Promise<any> => {
        const response = await fetch(`${publicDataDomain}${accountId}/forms/${formId}/registrations/validate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        // the body can be empty or plain text, so read it as text and parse JSON only when possible
        const responseText = await response.text();
        let responseBody: any = responseText;
        try {
            responseBody = responseText ? JSON.parse(responseText) : null;
        } catch {
            // keep the raw text
        }

        return { status: response.status, ...(responseBody ?? {}) };
    };

    const fetchFormTemplate = async ({ accountId, formId }: FormTemplateProps) => {
        const response = await fetch(`${publicDataDomain}${accountId}/forms/${formId}`);
        const data = await response.json();
        return data;
    };

    useEffect(() => {
        if (!(accountId && formId)) {
            setIsValidatingToken(false);
            setShowRegistrationForm(false);
            return;
        }

        if (!token) {
            setIsTokenValid(false);
            setIsValidatingToken(false);
            return;
        }

        let isCancelled = false;
        setIsValidatingToken(true);
        validateRegistrationToken({ accountId, formId, payload: { token } })
            .then((response: any) => {
                if (isCancelled) {
                    return;
                }
                const isTokenValid = !!response.valid || response.reason === "EventNotStarted";
                setIsTokenValid(isTokenValid);
            })
            .catch((error: unknown) => {
                if (isCancelled) {
                    return;
                }
                // eslint-disable-next-line no-console
                console.error('Failed to validate registration token', error);
                setIsTokenValid(false);
            })
            .finally(() => {
                !isCancelled && setIsValidatingToken(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [accountId, formId, token]);

    useEffect(() => {
        const fetchKey = `${accountId}:${formId}`;
        if (!(accountId && formId) || lastFetchedKeyRef.current === fetchKey) {
            return;
        }

        // without a form template the form can't be shown, so hide it to stop the player staying hidden while showRegistrationForm is undefined
        fetchFormTemplate({ accountId, formId })
            .then((data: formTemplateType) => {
                lastFetchedKeyRef.current = fetchKey;
                if (!data || typeof data === 'string') {
                    setShowRegistrationForm(false);
                    return;
                }
                setFormTemplate(data);
            })
            .catch((error: unknown) => {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch registration form template', error);
                setShowRegistrationForm(false);
            });
    }, [accountId, formId]);

    return {
        currentMediaData,
        formId,
        hasToken: !!token,
        isTokenValid,
        hasRegistration,
        hasEventStarted,
        mediaDescription,
        eventDate: currentMediaData?.custom?.['event-start'],
        isValidatingToken,
        formTemplate
    }
};

export default useRegistrationGuard;
